import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";
import { PROJECTS, LOGO_URL } from "../../lib/projects";
import AwardLogoStrip from "./AwardLogoStrip";

const SLIDE_MS = 5000;
const LOGO_MS = 3600;

// Build a background-friendly YouTube embed URL.
// autoplay+mute is required by every modern browser for unattended playback.
// loop+playlist=ID forces YT to loop a single video.
// controls/modestbranding/disablekb/iv_load_policy/fs strip every UI chrome
// so the iframe reads as pure motion footage.
const buildYouTubeSrc = (id, start = 0) =>
    `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${id}&start=${start}&modestbranding=1&playsinline=1&rel=0&disablekb=1&iv_load_policy=3&fs=0&showinfo=0&cc_load_policy=0`;

// Sauvage.tv-style fullscreen auto-advancing project reel.
// Every slide is a real moving asset — MP4 for the Nike hero,
// muted YouTube iframes for the rest. No static thumbnails up front.
export const ReelShowcase = ({ active = true }) => {
    const [idx, setIdx] = useState(0);
    const [hoverPaused, setHoverPaused] = useState(false);
    // Start unmuted so the Nike MP4 plays with audio when the browser allows
    // (e.g. when the user has prior media engagement on this origin).
    // After the first full playthrough we permanently mute it (see effect below).
    const [muted, setMuted] = useState(false);
    // Locks audio off after the Nike clip has played once. Survives
    // remounts of the slide because it is module-level state via ref.
    const firstPlayCompletedRef = useRef(false);
    const videoRef = useRef(null);
    const projectCount = PROJECTS.length;
    const total = projectCount + 1; // +1 for logo outro
    const isLogoSlide = idx === projectCount;
    const current = isLogoSlide ? null : PROJECTS[idx];

    // Per-slide duration (honor per-project duration override if present).
    const slideDuration = isLogoSlide
        ? LOGO_MS
        : (current && current.duration) || SLIDE_MS;

    // Don't pause auto-advance when hovering a video slide — keeps the reel
    // moving while playback continues in the background.
    const paused = !active || (hoverPaused && !(current && (current.video || current.youtubeId)));

    // Auto-advance
    useEffect(() => {
        if (paused) return undefined;
        const t = setTimeout(() => setIdx((i) => (i + 1) % total), slideDuration);
        return () => clearTimeout(t);
    }, [idx, paused, total, slideDuration]);

    // Try unmuted autoplay first; if the browser rejects it, fall back to
    // muted autoplay so the picture still plays. After the first full cycle
    // of the Nike MP4 we permanently silence it — the reel never plays the
    // Nike audio again, even when the slide loops back into view.
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return undefined;
        const shouldStartMuted = firstPlayCompletedRef.current;
        v.muted = shouldStartMuted;
        v.volume = 1;
        if (shouldStartMuted) setMuted(true);
        const p = v.play();
        if (p && typeof p.catch === "function") {
            p.catch(() => {
                // Browser blocked unmuted autoplay → quietly mute and retry.
                v.muted = true;
                setMuted(true);
                const p2 = v.play();
                if (p2 && typeof p2.catch === "function") p2.catch(() => {});
            });
        }

        // Detect end-of-first-play. Because the element has the `loop` attr,
        // `ended` does not fire — instead we watch for currentTime resetting
        // back near 0 after having advanced past a meaningful threshold.
        let lastCt = 0;
        const onTimeUpdate = () => {
            const ct = v.currentTime;
            // Loop boundary: ct dropped from "near end" back to "near start".
            if (lastCt > 1.5 && ct < 0.5 && !firstPlayCompletedRef.current) {
                firstPlayCompletedRef.current = true;
                v.muted = true;
                setMuted(true);
            }
            lastCt = ct;
        };
        v.addEventListener("timeupdate", onTimeUpdate);
        return () => v.removeEventListener("timeupdate", onTimeUpdate);
    }, [idx]);

    // First user gesture → unmute (only if first-play hasn't completed yet).
    useEffect(() => {
        let unmuted = false;
        const tryUnmute = () => {
            if (unmuted || firstPlayCompletedRef.current) return;
            const v = videoRef.current;
            if (v) {
                v.muted = false;
                v.volume = 1;
                const p = v.play();
                if (p && typeof p.catch === "function") p.catch(() => {});
                setMuted(false);
                unmuted = true;
            }
        };
        const handler = () => tryUnmute();
        window.addEventListener("pointerdown", handler, { passive: true });
        window.addEventListener("keydown", handler);
        return () => {
            window.removeEventListener("pointerdown", handler);
            window.removeEventListener("keydown", handler);
        };
    }, []);

    const go = (dir) => {
        setIdx((i) => (i + dir + total) % total);
    };

    return (
        <section
            id="top"
            data-testid="reel-showcase"
            className="relative isolate flex h-[100svh] w-full flex-col overflow-hidden bg-ink"
            onMouseEnter={() => setHoverPaused(true)}
            onMouseLeave={() => setHoverPaused(false)}
        >
            {/* Inner canvas — outline border only, rounded, with stylish corner ticks.
                The award strip sits BELOW this canvas as an attached footer band, so
                the canvas only takes the upper portion of the viewport. */}
            <div className="relative mx-3 mt-3 flex-1 overflow-hidden rounded-t-[18px] bg-ink md:mx-6 md:mt-6 md:rounded-t-[28px]">
            {/* Slides */}
            <AnimatePresence mode="sync">
                {isLogoSlide ? (
                    <motion.div
                        key="logo-outro"
                        data-testid="reel-slide-logo"
                        className="absolute inset-0 flex items-center justify-center bg-ink"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {/* Subtle cinematic backdrop */}
                        <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0"
                            style={{
                                background:
                                    "radial-gradient(800px 350px at 50% 50%, rgba(255,255,255,0.05), transparent 60%)"
                            }}
                        />

                        {/* Small centered logo */}
                        <div className="relative z-10 flex flex-col items-center">
                            <motion.img
                                src={LOGO_URL}
                                alt="Mad House Studios"
                                draggable={false}
                                initial={{ opacity: 0, scale: 0.94, filter: "blur(8px)" }}
                                animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                                className="h-24 w-auto select-none md:h-32"
                            />
                            <motion.p
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.25, duration: 0.6 }}
                                className="mt-6 font-mono text-[10px] uppercase tracking-[0.4em] text-white/60 md:text-xs"
                            >
                                Post. <span className="italic text-[#DB162F]">VFX.</span> Madness.
                            </motion.p>
                        </div>
                    </motion.div>
                ) : (
                    <motion.div
                        key={current.id}
                        data-testid={`reel-slide-${current.id}`}
                        className="absolute inset-0 overflow-hidden"
                        initial={{ opacity: 0, scale: 1.03 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.01 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    >
                        {current.video ? (
                            current.aspect === "9:16" ? (
                                // Portrait clip: render as a centered 9:16
                                // pillar so the actual frame is shown
                                // unstretched. A heavily-blurred copy of the
                                // same video fills the side gutters so the
                                // canvas never reads as a black mask.
                                <>
                                    <video
                                        aria-hidden
                                        className="absolute inset-0 h-full w-full scale-110 object-cover opacity-50 blur-2xl"
                                        src={current.video}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="auto"
                                    />
                                    <div
                                        aria-hidden
                                        className="pointer-events-none absolute inset-0"
                                        style={{
                                            background:
                                                "radial-gradient(ellipse at center, rgba(5,5,5,0.0) 30%, rgba(5,5,5,0.55) 100%)"
                                        }}
                                    />
                                    <video
                                        ref={current.hasAudio ? videoRef : null}
                                        data-testid="reel-video"
                                        className="absolute inset-y-0 left-1/2 z-10 h-full w-auto -translate-x-1/2 object-contain"
                                        src={current.video}
                                        autoPlay
                                        muted={current.hasAudio ? muted : true}
                                        loop
                                        playsInline
                                        preload="auto"
                                        poster={current.image}
                                    />
                                </>
                            ) : (
                                <video
                                    ref={current.hasAudio ? videoRef : null}
                                    data-testid="reel-video"
                                    className="absolute inset-0 h-full w-full object-cover"
                                    src={current.video}
                                    autoPlay
                                    muted={current.hasAudio ? muted : true}
                                    loop
                                    playsInline
                                    preload="auto"
                                    poster={current.image}
                                />
                            )
                        ) : current.youtubeId ? (
                            // Scaled iframe trick: oversize the iframe so YouTube's
                            // letterboxing is pushed outside the visible frame and the
                            // video reads as a true full-bleed background plate.
                            <div className="absolute inset-0 overflow-hidden">
                                <iframe
                                    key={current.id}
                                    data-testid={`reel-iframe-${current.id}`}
                                    title={current.title}
                                    src={buildYouTubeSrc(current.youtubeId, current.start || 0)}
                                    allow="autoplay; encrypted-media; picture-in-picture"
                                    allowFullScreen={false}
                                    frameBorder="0"
                                    className="pointer-events-none absolute left-1/2 top-1/2 h-[120vw] w-[213vh] min-h-full min-w-full -translate-x-1/2 -translate-y-1/2"
                                    loading="eager"
                                />
                            </div>
                        ) : (
                            <img
                                src={current.image}
                                alt={`${current.title} — ${current.subtitle || current.category}`}
                                className="kenburns absolute inset-0 h-full w-full object-cover"
                                draggable={false}
                            />
                        )}

                        <div
                            aria-hidden
                            className="pointer-events-none absolute inset-0"
                            style={{
                                background:
                                    "linear-gradient(to top, rgba(5,5,5,0.7) 0%, rgba(5,5,5,0.12) 32%, rgba(5,5,5,0.08) 68%, rgba(5,5,5,0.55) 100%)"
                            }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Minimal in-canvas controls: prev/next + scroll cue. The
                tagline lives BELOW the canvas as its own UI band so it
                never overlaps the video. */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex items-end justify-between px-6 pb-6 md:px-12 md:pb-8">
                <div className="pointer-events-auto flex items-center gap-2">
                    <button
                        type="button"
                        aria-label="Previous slide"
                        data-testid="reel-prev"
                        data-cursor="hover"
                        onClick={() => go(-1)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-white/30 text-white/80 transition-colors hover:border-white hover:bg-white hover:text-ink md:h-9 md:w-9"
                    >
                        <ChevronLeft size={14} />
                    </button>
                    <button
                        type="button"
                        aria-label="Next slide"
                        data-testid="reel-next"
                        data-cursor="hover"
                        onClick={() => go(1)}
                        className="flex h-8 w-8 items-center justify-center rounded-full border border-white/30 text-white/80 transition-colors hover:border-white hover:bg-white hover:text-ink md:h-9 md:w-9"
                    >
                        <ChevronRight size={14} />
                    </button>
                </div>
                <div className="hidden font-mono text-[9px] uppercase tracking-[0.3em] text-white/60 md:flex md:items-center md:gap-2">
                    <ArrowDown size={10} className="animate-bounce" />
                    Scroll
                </div>
            </div>
            </div>
            {/* End inner canvas */}

            {/* Tagline UI band — sits in the gap between the video canvas and
                the award strip. Shares the same red borders + horizontal
                margins as both, so it reads as a chrome strip of the slide
                placeholder UI rather than overlay text on the video. */}
            <div
                data-testid="hero-tagline"
                className="relative mx-3 flex items-center justify-center overflow-hidden bg-ink-surface/85 px-4 py-1.5 backdrop-blur-md md:mx-6 md:py-2"
            >
                {/* Faint top + bottom hairlines for the chrome feel */}
                <span
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 top-0 h-px"
                    style={{
                        background:
                            "linear-gradient(90deg, transparent 0%, rgba(238,200,110,0.35) 50%, transparent 100%)"
                    }}
                />
                <span
                    aria-hidden
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
                    style={{
                        background:
                            "linear-gradient(90deg, transparent 0%, rgba(238,200,110,0.35) 50%, transparent 100%)"
                    }}
                />

                <div className="flex items-center gap-2.5 md:gap-4">
                    <span
                        aria-hidden
                        className="inline-block h-[3px] w-[3px] rotate-45"
                        style={{
                            background: "#eec86e",
                            boxShadow: "0 0 6px rgba(238,200,110,0.6)"
                        }}
                    />
                    <motion.span
                        initial={{ backgroundPosition: "200% 0%" }}
                        animate={{ backgroundPosition: ["200% 0%", "-100% 0%"] }}
                        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                        className="font-mono uppercase italic leading-none"
                        style={{
                            fontSize: "clamp(0.55rem, 0.82vw, 0.72rem)",
                            fontWeight: 300,
                            background:
                                "linear-gradient(110deg, #b6781f 0%, #f4d691 35%, #ffffff 50%, #f4d691 65%, #b6781f 100%)",
                            backgroundSize: "200% 100%",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                            color: "transparent",
                            letterSpacing: "0.32em",
                            textIndent: "0.32em"
                        }}
                    >
                        Award-Winning Post Studio
                    </motion.span>
                    <span
                        aria-hidden
                        className="inline-block h-[3px] w-[3px] rotate-45"
                        style={{
                            background: "#eec86e",
                            boxShadow: "0 0 6px rgba(238,200,110,0.6)"
                        }}
                    />
                </div>
            </div>

            {/* Award footer band — visually attached to the slide frame.
                Shares the same horizontal margins + red border so it reads
                as a docked footer of the placeholder, not a separate section. */}
            <div className="relative mx-3 mb-3 overflow-hidden rounded-b-[18px] bg-ink-surface/55 backdrop-blur-md md:mx-6 md:mb-6 md:rounded-b-[28px]">
                <AwardLogoStrip embedded />
            </div>
        </section>
    );
};

export default ReelShowcase;
